import { useCallback, useEffect } from 'react'
import { validateTemporaryToken } from '../../../infra/utils/validateToken'
import { ERROR_TYPES } from '../../../infra/constants/ErrorTypes'
import { getUrlToken } from './useGetURLToken'

let validationInFlight = null
let currentValidToken = null

const AUTH_TOKEN_KEY = 'authToken'

const ensureValidTokenCore = async (revalidateExisting = false, signal) => {
    if (currentValidToken && !revalidateExisting)
        return { token: currentValidToken }

    if (validationInFlight) {
        try {
            const token = await validationInFlight
            return { token }
        } catch (error) {
            validationInFlight = null
        }
    }

    const validationPromise = (async () => {
        try {
            // Try localStorage token first
            const sessionToken = localStorage.getItem(AUTH_TOKEN_KEY)

            if (sessionToken && (!revalidateExisting || currentValidToken !== sessionToken)) {
                const isValidStored = await validateTemporaryToken(sessionToken, signal)

                if (isValidStored) {
                    currentValidToken = sessionToken
                    return sessionToken
                } else {
                    // Invalid stored token, remove it
                    localStorage.removeItem(AUTH_TOKEN_KEY)
                    currentValidToken = null
                }
            }

            // Try URL token if localStorage is empty/invalid
            const urlToken = getUrlToken()

            if (urlToken) {
                const isValidUrl = await validateTemporaryToken(urlToken, signal)
                if (isValidUrl) {
                    localStorage.setItem(AUTH_TOKEN_KEY, urlToken)
                    currentValidToken = urlToken
                    return urlToken
                }
            }

            throw new Error('No valid token found')
        } finally {
            validationInFlight = null
        }
    })()

    validationInFlight = validationPromise

    try {
        const token = await validationPromise
        return { token }
    } catch (error) {
        const tokenError = {
            type: ERROR_TYPES.INVALID,
            message: error.message
        }
        throw tokenError
    }
}

const useEnsureValidToken = () => {
    const ensureValidToken = useCallback(
        (revalidateExisting = false, signal) =>
            ensureValidTokenCore(revalidateExisting, signal),
        []
    )

    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === AUTH_TOKEN_KEY && e.newValue === null) {
                currentValidToken = null
                validationInFlight = null
            }
        }

        window.addEventListener('storage', handleStorageChange)
        return () => window.removeEventListener('storage', handleStorageChange)
    }, [])

    return { ensureValidToken }
}

export default useEnsureValidToken